import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Row, Col, Label, Input } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { format } from 'date-fns';
import 'react-toastify/dist/ReactToastify.css';

// Import constants
import { COUNTRIES } from '../../constants/countries';
import { GENDER_OPTIONS } from '../../constants/formOptions';

// Import utilities
import { getCountryCode } from '../../utils/phoneUtils';
import { sanitizeValue } from '../../utils/dataFormatters';

// Import validations
import { validatePersonalInfo } from '../../validations/personalInfoValidation';

const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
	<div className="custom-datepicker-input custom_date" onClick={onClick} ref={ref}>
		<input type="text" value={value} readOnly placeholder="DD/MM/YYYY" className="form-control" />
		<img src="/assets/images/calendar.svg" className="cal_addicon" alt="calendar icon" />
	</div>
));

const PersonalInfoForm = forwardRef(({ data, onChange, showValidation, setIsFormValid, isDocumentUploaded }, ref) => {
	const [formData, setFormData] = useState({
		firstName: '',
		middleName: '',
		lastName: '',
		dateOfBirth: new Date(),
		gender: '',
		nationality: '',
		email: '',
		phone: '',
		mailingAddress: {
			address: '',
			city: '',
			state: '',
			country: '',
			zipCode: '',
		},
		useMailingAsPermanent: false, // Ensure unchecked by default
		permanentAddress: {
			address: '',
			city: '',
			state: '',
			country: '',
			zipCode: '',
		},
	});

	const [errors, setErrors] = useState({});
	const [phoneInputKey, setPhoneInputKey] = useState(0);
	const previousPhoneRef = useRef('');

	// Load saved data when component mounts or data changes, and always clean/format phone
	const isFirstLoad = useRef(true);
	useEffect(() => {
		if (data) {
			// Always clean/format phone for PhoneInput (digits only)
			const cleanedPhone = data.phone ? String(data.phone).replace(/\D/g, '') : '';
			
			// Only remount PhoneInput if phone changed from populated to empty (external clear)
			// This resets the country code without disrupting user typing
			console.log('Previous phone:', previousPhoneRef.current, 'Cleaned phone:', cleanedPhone, 'Data phone:', data.phone, phoneInputKey);
			if (previousPhoneRef.current && !cleanedPhone) {
				setPhoneInputKey(prev => prev + 1);
			}
			if(!previousPhoneRef.current && cleanedPhone) {
				setPhoneInputKey(prev => prev + 1);
			}
			previousPhoneRef.current = cleanedPhone;
			
			setFormData((prev) => ({
				...prev,
				...data,
				phone: cleanedPhone,
				useMailingAsPermanent: isFirstLoad.current ? false : prev.useMailingAsPermanent,
			}));
			isFirstLoad.current = false;
		}
	}, [data]);

	const handleChange = (field, value) => {
		const sanitizedValue = sanitizeValue(value);
		const updatedData = {
			...formData,
			[field]: sanitizedValue,
		};

		setFormData(updatedData);
		onChange(updatedData);
	};

	const handleMailingAddressChange = (field, value) => {
		const sanitizedValue = sanitizeValue(value);
		const updatedData = {
			...formData,
			mailingAddress: {
				...formData.mailingAddress,
				[field]: sanitizedValue,
			},
			// If useMailingAsPermanent is checked, update permanent address as well
			permanentAddress: formData.useMailingAsPermanent
				? {
						...formData.mailingAddress,
						[field]: sanitizedValue,
				  }
				: formData.permanentAddress,
		};
		setFormData(updatedData);
		onChange(updatedData);
	};

	const handlePermanentAddressChange = (field, value) => {
		const sanitizedValue = sanitizeValue(value);
		const updatedData = {
			...formData,
			permanentAddress: {
				...formData.permanentAddress,
				[field]: sanitizedValue,
			},
		};
		setFormData(updatedData);
		onChange(updatedData);
	};

	const handleUseMailingAsPermanent = (checked) => {
		const updatedData = {
			...formData,
			useMailingAsPermanent: checked,
			permanentAddress: checked ? { ...formData.mailingAddress } : formData.permanentAddress,
		};
		setFormData(updatedData);
		onChange(updatedData);
	};

	// Validation function using centralized validation utility
	const validate = () => {
		const newErrors = validatePersonalInfo(formData);
		setErrors(newErrors);
		if (setIsFormValid) setIsFormValid(Object.keys(newErrors).length === 0);
		return Object.keys(newErrors).length === 0;
	};

	useEffect(() => {
		if (showValidation) validate();
		// eslint-disable-next-line
	}, [showValidation, formData]);

	useImperativeHandle(ref, () => ({
		validate,
	}));

	// Track if data has been loaded and show toast if needed
	// useEffect(() => {
	//   if (formData.firstName === "") {
	//     isDataLoading.current = false;
	//     // Only show toast if no document and no data
	//     if (!isDocumentUploaded && !formData?.firstName) {
	//       toast.info(INFO_MESSAGES.UPLOAD_DOCUMENT_PROMPT);
	//     }
	//   }
	// }, [isDocumentUploaded, formData]);
	console.log('🚀 ~ file: PersonalInfoForm.jsx:295 ~ PersonalInfoForm ~ formData:', formData.phone);
	return (
		<>
			{isDocumentUploaded && (
				<div className="py-4">
					{/* <ToastContainer position="bottom-right  " autoClose={3000} /> */}
					<Row>
						<Col xl="4">
							<div className="personal_formdetail">
								<h5>
									First Name <span>*</span>
								</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.firstName ? ' is-invalid' : ''}`}
									placeholder="First Name"
									value={formData.firstName}
									onChange={(e) => handleChange('firstName', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								<div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px' }}>
									Please do not use any Prefix like Mr./Ms./Dr./Prof. before your name
								</div>
								{errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>Middle Name</h5>
								<input
									type="text"
									className="form-control form_custom"
									placeholder="Second Name"
									value={formData.middleName}
									onChange={(e) => handleChange('middleName', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>
									Last Name <span>*</span>
								</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.lastName ? ' is-invalid' : ''}`}
									placeholder="Last Name"
									value={formData.lastName}
									onChange={(e) => handleChange('lastName', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								{errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
							</div>
						</Col>

						<Col xl="4">
							<div className="react_date_wrap">
								<h5>
									Date Of Birth <span>*</span>
								</h5>
								<DatePicker
									selected={formData.dateOfBirth}
									onChange={(date) => handleChange('dateOfBirth', date)}
									dateFormat="dd/MM/yyyy"
									showYearDropdown
									maxDate={new Date()}
									showMonthDropdown
									dropdownMode="select"
									customInput={
										<CustomInput
											value={formData.dateOfBirth ? format(formData.dateOfBirth, 'dd/MM/yyyy') : ''}
											onClick={(e) => e.stopPropagation()}
										/>
									}
									disabled={!isDocumentUploaded}
								/>
								{showValidation && errors.dateOfBirth && (
									<div className="invalid-feedback d-block">{errors.dateOfBirth}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>
									Gender <span>*</span>
								</h5>
								<select
									className={`select_gender${errors.gender ? ' is-invalid' : ''}`}
									value={formData.gender}
									onChange={(e) => handleChange('gender', e.target.value)}
									disabled={!isDocumentUploaded}>
									{GENDER_OPTIONS.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
								{errors.gender && <div className="invalid-feedback d-block">{errors.gender}</div>}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>
									Nationality <span>*</span>
								</h5>
								<select
									className={`select_gender${errors.nationality ? ' is-invalid' : ''}`}
									value={formData.nationality}
									onChange={(e) => handleChange('nationality', e.target.value)}
									disabled={!isDocumentUploaded}>
									<option value="">Select Nationality</option>
									{COUNTRIES.map((country) => (
										<option key={country} value={country}>
											{country}
										</option>
									))}
								</select>
								{errors.nationality && <div className="invalid-feedback d-block">{errors.nationality}</div>}
							</div>
						</Col>

						<Col xl="6">
							<div className="personal_formdetail">
								<h5>
									Email Address <span>*</span>
								</h5>
								<input
									type="email"
									className={`form-control form_cap form_custom${errors.email ? ' is-invalid' : ''}`}
									placeholder="Email"
									value={formData.email}
									disabled
									onChange={(e) => handleChange('email', e.target.value)}
								/>
								{errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
							</div>
						</Col>

						<Col xl="6">
							<div className="personal_formdetail">
								<h5>
									Phone Number <span>*</span>
								</h5>
								<div className="phone_input">
									<PhoneInput
										// Only remount when phoneInputKey changes (when phone is cleared externally)
										key={phoneInputKey}
										country={
											formData.phone
												? getCountryCode(formData.phone) ||
												  (formData.nationality === 'India'
														? 'in'
														: formData.nationality
														? formData.nationality.toLowerCase().slice(0, 2)
														: 'in')
												: formData.nationality === 'India'
												? 'in'
												: formData.nationality
												? formData.nationality.toLowerCase().slice(0, 2)
												: 'in'
										}
										countryCodeEditable={false}
										value={formData.phone}
										onChange={(phone, country) => {
											// phone is always digits only from PhoneInput
											handleChange('phone', phone);
										}}
										inputProps={{
											name: 'phone',
											required: true,
											disabled: !isDocumentUploaded,
										}}
									/>
								</div>
								{errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
							</div>
						</Col>
					</Row>

					<Row>
						<Col xl="4">
							<div className="personal_formdetail">
								<h5>
									Mailing Address <span>*</span>
								</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.mailingAddress_address ? ' is-invalid' : ''}`}
									placeholder="Address"
									value={formData.mailingAddress.address}
									onChange={(e) => handleMailingAddressChange('address', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								{errors.mailingAddress_address && (
									<div className="invalid-feedback d-block">{errors.mailingAddress_address}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>&nbsp;</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.mailingAddress_city ? ' is-invalid' : ''}`}
									placeholder="City"
									value={formData.mailingAddress.city}
									onChange={(e) => handleMailingAddressChange('city', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								{errors.mailingAddress_city && (
									<div className="invalid-feedback d-block">{errors.mailingAddress_city}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>&nbsp;</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.mailingAddress_state ? ' is-invalid' : ''}`}
									placeholder="State"
									value={formData.mailingAddress.state}
									onChange={(e) => handleMailingAddressChange('state', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								{errors.mailingAddress_state && (
									<div className="invalid-feedback d-block">{errors.mailingAddress_state}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<input
									type="text"
									className={`form-control form_custom${errors.mailingAddress_country ? ' is-invalid' : ''}`}
									placeholder="Country"
									value={formData.mailingAddress.country}
									onChange={(e) => handleMailingAddressChange('country', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								{errors.mailingAddress_country && (
									<div className="invalid-feedback d-block">{errors.mailingAddress_country}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<input
									type="number"
									className={`form-control form_custom${errors.mailingAddress_zipCode ? ' is-invalid' : ''}`}
									placeholder="Zip Code"
									value={formData.mailingAddress.zipCode}
									onChange={(e) => handleMailingAddressChange('zipCode', e.target.value)}
									disabled={!isDocumentUploaded}
								/>
								{errors.mailingAddress_zipCode && (
									<div className="invalid-feedback d-block">{errors.mailingAddress_zipCode}</div>
								)}
							</div>
						</Col>
					</Row>

					<Row>
						<Col xl="12">
							<div className="rem_input custom-checkbox custom-control">
								{console.log(
									'🚀 ~ file: PersonalInfoForm.jsx:823 ~ PersonalInfoForm ~ formData.useMailingAsPermanent:',
									formData.useMailingAsPermanent
								)}
								<Input
									type="checkbox"
									id="same"
									className="custom-control-input"
									checked={formData.useMailingAsPermanent}
									onChange={(e) => handleUseMailingAsPermanent(e.target.checked)}
								/>
								<Label className="custom-control-label" htmlFor="same">
									Use my mailing address as my permanent address.
								</Label>
							</div>
						</Col>
					</Row>

					<Row>
						<Col xl="4">
							<div className="personal_formdetail">
								<h5>
									Permanent Address<span>*</span>
								</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.permanentAddress_address ? ' is-invalid' : ''}`}
									placeholder="Address"
									value={formData.permanentAddress.address}
									onChange={(e) => handlePermanentAddressChange('address', e.target.value)}
									disabled={formData.useMailingAsPermanent || !isDocumentUploaded}
								/>
								{errors.permanentAddress_address && (
									<div className="invalid-feedback d-block">{errors.permanentAddress_address}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>&nbsp;</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.permanentAddress_city ? ' is-invalid' : ''}`}
									placeholder="City"
									value={formData.permanentAddress.city}
									onChange={(e) => handlePermanentAddressChange('city', e.target.value)}
									disabled={formData.useMailingAsPermanent || !isDocumentUploaded}
								/>
								{errors.permanentAddress_city && (
									<div className="invalid-feedback d-block">{errors.permanentAddress_city}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<h5>&nbsp;</h5>
								<input
									type="text"
									className={`form-control form_custom${errors.permanentAddress_state ? ' is-invalid' : ''}`}
									placeholder="State"
									value={formData.permanentAddress.state}
									onChange={(e) => handlePermanentAddressChange('state', e.target.value)}
									disabled={formData.useMailingAsPermanent || !isDocumentUploaded}
								/>
								{errors.permanentAddress_state && (
									<div className="invalid-feedback d-block">{errors.permanentAddress_state}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<input
									type="text"
									className={`form-control form_custom${errors.permanentAddress_country ? ' is-invalid' : ''}`}
									placeholder="Country"
									value={formData.permanentAddress.country}
									onChange={(e) => handlePermanentAddressChange('country', e.target.value)}
									disabled={formData.useMailingAsPermanent || !isDocumentUploaded}
								/>
								{errors.permanentAddress_country && (
									<div className="invalid-feedback d-block">{errors.permanentAddress_country}</div>
								)}
							</div>
						</Col>

						<Col xl="4">
							<div className="personal_formdetail">
								<input
									type="number"
									className={`form-control form_custom${errors.permanentAddress_zipCode ? ' is-invalid' : ''}`}
									placeholder="Zip Code"
									value={formData.permanentAddress.zipCode}
									onChange={(e) => handlePermanentAddressChange('zipCode', e.target.value)}
									disabled={formData.useMailingAsPermanent || !isDocumentUploaded}
								/>
								{errors.permanentAddress_zipCode && (
									<div className="invalid-feedback d-block">{errors.permanentAddress_zipCode}</div>
								)}
							</div>
						</Col>
					</Row>
				</div>
			)}
		</>
	);
});

export default PersonalInfoForm;
